'use client';

import { useState, useCallback, useEffect } from 'react';
import { Copy, RefreshCw, Check, Eye, EyeOff, Lock } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { passwordGeneratorContent } from '../../data/password-generator-content';
import Link from 'next/link';

type PasswordOptions = {
  length: number;
  uppercase: boolean;
  lowercase: boolean;
  numbers: boolean;
  symbols: boolean;
};

const LOWERCASE_CHARS = 'abcdefghijklmnopqrstuvwxyz';
const UPPERCASE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const NUMBER_CHARS = '0123456789';
const SYMBOL_CHARS = '!@#$%^&*()_+-=[]{}|;:,.<>?';

export default function PasswordGenerator() {
  const [password, setPassword] = useState<string>('');
  const [options, setOptions] = useState<PasswordOptions>({
    length: 12,
    uppercase: true,
    lowercase: true,
    numbers: true,
    symbols: true,
  });
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [strength, setStrength] = useState<{ score: number; label: string; color: string }>({
    score: 0,
    label: '',
    color: 'bg-gray-200 dark:bg-gray-700'
  });

  // Generate a random password based on options
  const generatePassword = useCallback(() => {
    let chars = '';
    const { length, uppercase, lowercase, numbers, symbols } = options;

    if (uppercase) chars += UPPERCASE_CHARS;
    if (lowercase) chars += LOWERCASE_CHARS;
    if (numbers) chars += NUMBER_CHARS;
    if (symbols) chars += SYMBOL_CHARS;

    // If no character types are selected, use all
    if (!chars) {
      chars = LOWERCASE_CHARS + UPPERCASE_CHARS + NUMBER_CHARS + SYMBOL_CHARS;
    }

    let result = '';
    const charsLength = chars.length;

    // Generate password with at least one character from each selected character set
    const selectedChars = [];
    if (uppercase) selectedChars.push(getRandomChar(UPPERCASE_CHARS));
    if (lowercase) selectedChars.push(getRandomChar(LOWERCASE_CHARS));
    if (numbers) selectedChars.push(getRandomChar(NUMBER_CHARS));
    if (symbols) selectedChars.push(getRandomChar(SYMBOL_CHARS));

    // Fill the rest of the password with random characters
    for (let i = selectedChars.length; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * charsLength));
    }

    // Add the required characters and shuffle
    result = shuffleString(selectedChars.join('') + result).slice(0, length);

    setPassword(result);
    calculateStrength(result);
  }, [options]);

  // Helper function to get a random character from a string
  const getRandomChar = (chars: string): string => {
    return chars.charAt(Math.floor(Math.random() * chars.length));
  };

  // Shuffle a string
  const shuffleString = (str: string): string => {
    const array = str.split('');
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array.join('');
  };

  // Calculate password strength
  const calculateStrength = (pwd: string) => {
    let score = 0;
    let label = '';
    let color = '';

    // Length check
    if (pwd.length >= 12) score += 2;
    else if (pwd.length >= 8) score += 1;

    // Character variety
    if (/[a-z]/.test(pwd)) score += 1; // Lowercase
    if (/[A-Z]/.test(pwd)) score += 1; // Uppercase
    if (/[0-9]/.test(pwd)) score += 1; // Numbers
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1; // Symbols

    // Common patterns (penalize)
    if (/(.)\1{2,}/.test(pwd)) score -= 1; // Repeated characters
    if (/123|abc|qwerty|password/i.test(pwd)) score = Math.max(0, score - 2);

    // Set strength level
    if (pwd.length === 0) {
      label = '';
      color = 'bg-gray-200 dark:bg-gray-700';
    } else if (score <= 2) {
      label = 'Weak';
      color = 'bg-red-500';
    } else if (score <= 4) {
      label = 'Fair';
      color = 'bg-yellow-500';
    } else if (score <= 6) {
      label = 'Good';
      color = 'bg-blue-500';
    } else {
      label = 'Strong';
      color = 'bg-green-500';
    }

    setStrength({ score, label, color });
  };

  // Copy password to clipboard
  const copyToClipboard = () => {
    if (!password) return;

    navigator.clipboard.writeText(password).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  // Generate initial password on component mount
  useEffect(() => {
    generatePassword();
  }, [generatePassword]);

  // Handle option changes
  const handleOptionChange = (option: keyof PasswordOptions, value: any) => {
    setOptions(prev => ({
      ...prev,
      [option]: value,
    }));
  };

  // Ensure at least one character type is selected
  useEffect(() => {
    const { uppercase, lowercase, numbers, symbols } = options;
    if (!uppercase && !lowercase && !numbers && !symbols) {
      setOptions(prev => ({
        ...prev,
        lowercase: true,
      }));
    }
  }, [options]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 py-8 px-4 sm:py-12">
      <div className="max-w-md w-full mx-auto">
        <ToolPageIntro title="Password Generator">
          Create strong, random passwords with options for length, uppercase, lowercase, numbers, and symbols. Use it for new accounts, API keys, or any place you need a secure secret. No sign-up; all generation happens in your browser.
        </ToolPageIntro>

        {/* Password Display */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row items-stretch gap-3 mb-4">
            <div className="relative flex-grow">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                readOnly
                className="w-full bg-gray-50 dark:bg-gray-700 border-0 text-base sm:text-lg font-mono p-3 pr-10 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" />
                ) : (
                  <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                )}
              </button>
            </div>
            <div className="flex justify-between sm:justify-start gap-2">
              <button
                onClick={copyToClipboard}
                disabled={!password}
                className="p-2 sm:p-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                aria-label="Copy to clipboard"
              >
                {isCopied ? (
                  <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                ) : (
                  <Copy className="w-4 h-4 sm:w-5 sm:h-5" />
                )}
              </button>
              <button
                onClick={generatePassword}
                className="p-2 sm:p-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg transition-colors flex-shrink-0"
                aria-label="Generate new password"
              >
                <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700 dark:text-gray-200" />
              </button>
            </div>
          </div>

          {/* Password Strength */}
          <div className="mb-1">
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">
                Password Strength:
              </span>
              <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                {strength.label}
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${strength.color}`}
                style={{
                  width: password ? `${Math.min(100, (strength.score / 7) * 100)}%` : '0%',
                }}
              />
            </div>
          </div>
        </div>

        {/* Options */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 sm:p-6 mb-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">
            Customize
          </h2>

          {/* Password Length */}
          <div className="mb-5">
            <div className="flex justify-between items-center mb-2">
              <label htmlFor="length" className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                Length: <span className="font-mono text-sm">{options.length}</span>
              </label>
              <span className="text-xs text-gray-500">
                {options.length < 8 ? 'Weak' : options.length < 12 ? 'Good' : 'Strong'}
              </span>
            </div>
            <input
              type="range"
              id="length"
              min="4"
              max="32"
              value={options.length}
              onChange={(e) => handleOptionChange('length', parseInt(e.target.value))}
              className="w-full h-1.5 sm:h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-blue-500"
            />
          </div>

          {/* Character Types */}
          <div className="space-y-2.5">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={options.uppercase}
                onChange={(e) => handleOptionChange('uppercase', e.target.checked)}
                className="w-4 h-4 text-blue-500 rounded border-gray-300 focus:ring-blue-500 flex-shrink-0"
              />
              <span className="ml-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300">Uppercase (A-Z)</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={options.lowercase}
                onChange={(e) => handleOptionChange('lowercase', e.target.checked)}
                className="w-4 h-4 text-blue-500 rounded border-gray-300 focus:ring-blue-500 flex-shrink-0"
              />
              <span className="ml-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300">Lowercase (a-z)</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={options.numbers}
                onChange={(e) => handleOptionChange('numbers', e.target.checked)}
                className="w-4 h-4 text-blue-500 rounded border-gray-300 focus:ring-blue-500 flex-shrink-0"
              />
              <span className="ml-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300">Numbers (0-9)</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={options.symbols}
                onChange={(e) => handleOptionChange('symbols', e.target.checked)}
                className="w-4 h-4 text-blue-500 rounded border-gray-300 focus:ring-blue-500 flex-shrink-0"
              />
              <span className="ml-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300">Symbols (!@#$%^&*)</span>
            </label>
          </div>
        </div>

        {/* Generate Button */}
        <button
          onClick={generatePassword}
          className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-2.5 sm:py-3 px-4 rounded-lg shadow-md transition-colors flex items-center justify-center text-sm sm:text-base"
        >
          <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" />
          Generate New Password
        </button>

        {/* Password Tips */}
        <div className="mt-6 sm:mt-8 bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 sm:p-5">
          <h3 className="text-sm sm:text-base font-semibold text-blue-800 dark:text-blue-200 mb-2 sm:mb-3">
            Tips for a Strong Password:
          </h3>
          <ul className="text-xs sm:text-sm text-blue-700 dark:text-blue-300 space-y-1.5">
            <li className="flex items-start">
              <span className="mr-1.5">•</span>
              <span>Use at least 12 characters</span>
            </li>
            <li className="flex items-start">
              <span className="mr-1.5">•</span>
              <span>Include a mix of uppercase and lowercase letters</span>
            </li>
            <li className="flex items-start">
              <span className="mr-1.5">•</span>
              <span>Add numbers and special characters</span>
            </li>
            <li className="flex items-start">
              <span className="mr-1.5">•</span>
              <span>Avoid common words and patterns</span>
            </li>
            <li className="flex items-start">
              <span className="mr-1.5">•</span>
              <span>Don't reuse passwords across different sites</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Informational Content */}
      <div className="mt-12 max-w-4xl mx-auto">
        <InformationalContent
          title={passwordGeneratorContent.title}
          description={passwordGeneratorContent.description}
          content={passwordGeneratorContent.content}
          faqs={passwordGeneratorContent.faqs}
          relatedTools={passwordGeneratorContent.relatedTools}
        />
      </div>
    </div>
  );
}
